import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { DarkPage } from "@/components/app/PageHeader";
import { FileDown, Save, LayoutTemplate } from "lucide-react";

const LANGUAGES = [
  { value: "en", label: "English" },
  { value: "es", label: "Español" },
  { value: "fr", label: "Français" },
  { value: "de", label: "Deutsch" },
];

const DOC_TYPES = [
  { value: "invoice", label: "Invoices" },
  { value: "quote", label: "Quotes" },
  { value: "project_report", label: "Project reports" },
];

export default function PDFSettings() {
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [allTemplates, setAllTemplates] = useState({});

  const load = useCallback(async () => {
    try {
      const [settingsRes, builtinRes, customRes] = await Promise.all([
        api.get("/pdf-settings"),
        api.get("/pdf-templates/builtin"),
        api.get("/pdf-templates"),
      ]);
      setForm(settingsRes.data);
      const byType = {};
      for (const dt of DOC_TYPES) byType[dt.value] = [];
      for (const t of [...(builtinRes.data || []), ...(customRes.data || [])]) {
        if (!byType[t.doc_type]) byType[t.doc_type] = [];
        byType[t.doc_type].push(t);
      }
      setAllTemplates(byType);
    } catch {
      toast.error("Failed to load PDF settings");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.put("/pdf-settings", {
        primary_color: form.primary_color, accent_color: form.accent_color,
        language: form.language, footer_text: form.footer_text,
        company_display_name: form.company_display_name,
        selected_templates: form.selected_templates || {},
      });
      setForm(data);
      toast.success("PDF settings saved");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save PDF settings");
    } finally {
      setSaving(false);
    }
  };

  const setTemplateFor = (docType, templateId) => {
    setForm(f => ({ ...f, selected_templates: { ...(f.selected_templates || {}), [docType]: templateId } }));
  };

  if (!form) return <div className="p-6 max-w-2xl mx-auto text-sm text-[#7d8590]">Loading…</div>;

  return (
    <DarkPage><div className="p-6 max-w-2xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-white">PDF Customization</h1>
        <p className="text-sm text-[#7d8590] mt-1">Colors, language, and footer applied to every generated invoice, quote, and project report PDF.</p>
      </div>

      <div className="bg-[#0d1117] border border-[#21262d] rounded-lg p-6 space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="text-xs text-[#7d8590] mb-1 block">Header color</label>
            <div className="flex items-center gap-2">
              <input type="color" value={form.primary_color} onChange={e => setForm(f => ({ ...f, primary_color: e.target.value }))}
                className="w-10 h-9 bg-transparent border border-[#30363d] rounded-md cursor-pointer" />
              <input value={form.primary_color} onChange={e => setForm(f => ({ ...f, primary_color: e.target.value }))}
                className="flex-1 bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm font-mono" />
            </div>
          </div>
          <div>
            <label className="text-xs text-[#7d8590] mb-1 block">Accent color</label>
            <div className="flex items-center gap-2">
              <input type="color" value={form.accent_color} onChange={e => setForm(f => ({ ...f, accent_color: e.target.value }))}
                className="w-10 h-9 bg-transparent border border-[#30363d] rounded-md cursor-pointer" />
              <input value={form.accent_color} onChange={e => setForm(f => ({ ...f, accent_color: e.target.value }))}
                className="flex-1 bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm font-mono" />
            </div>
          </div>
        </div>

        <div>
          <label className="text-xs text-[#7d8590] mb-1 block">Document language</label>
          <select value={form.language} onChange={e => setForm(f => ({ ...f, language: e.target.value }))}
            className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm">
            {LANGUAGES.map(l => <option key={l.value} value={l.value}>{l.label}</option>)}
          </select>
        </div>

        <div>
          <label className="text-xs text-[#7d8590] mb-1 block">Company display name</label>
          <input value={form.company_display_name} onChange={e => setForm(f => ({ ...f, company_display_name: e.target.value }))}
            placeholder="Shown under the document title"
            className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm" />
        </div>

        <div>
          <label className="text-xs text-[#7d8590] mb-1 block">Footer text</label>
          <textarea value={form.footer_text} onChange={e => setForm(f => ({ ...f, footer_text: e.target.value }))} rows={2}
            placeholder="e.g. Registered address, VAT number, thank-you note…"
            className="w-full bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-2 text-sm resize-none" />
        </div>

        <div className="border-t border-[#21262d] pt-5">
          <div className="flex items-center justify-between mb-3">
            <label className="text-xs text-[#7d8590]">Layout per document type</label>
            <Link to="/pdf-templates" className="text-xs text-[#00F5D4] hover:underline flex items-center gap-1">
              <LayoutTemplate className="w-3.5 h-3.5" /> Manage custom templates
            </Link>
          </div>
          <div className="space-y-3">
            {DOC_TYPES.map(dt => (
              <div key={dt.value} className="flex items-center justify-between gap-3">
                <span className="text-sm text-white">{dt.label}</span>
                <select
                  value={(form.selected_templates || {})[dt.value] || "builtin:classic"}
                  onChange={e => setTemplateFor(dt.value, e.target.value)}
                  className="bg-[#161b22] border border-[#30363d] text-white rounded-md px-3 py-1.5 text-sm w-56"
                >
                  {(allTemplates[dt.value] || []).map(t => (
                    <option key={t.template_id} value={t.template_id}>{t.name}{t.builtin ? "" : " (custom)"}</option>
                  ))}
                </select>
              </div>
            ))}
          </div>
        </div>

        <div className="flex items-center gap-3 pt-2">
          <Button onClick={save} disabled={saving} className="bg-[#00F5D4] text-[#030712] hover:bg-[#00F5D4]/90 font-semibold">
            <Save className="w-4 h-4 mr-1.5" /> {saving ? "Saving…" : "Save settings"}
          </Button>
          <span className="text-xs text-[#7d8590] flex items-center gap-1.5">
            <FileDown className="w-3.5 h-3.5" /> Applies to invoice, quote, and project report PDFs immediately.
          </span>
        </div>
      </div>

      {/* Live preview swatch */}
      <div className="mt-6 rounded-lg overflow-hidden border border-[#21262d]">
        <div className="h-16 flex items-center px-5" style={{ background: form.primary_color }}>
          <span className="text-lg font-bold tracking-wide" style={{ color: form.accent_color }}>
            {LANGUAGES.find(l => l.value === form.language)?.value === "en" ? "INVOICE" : "PREVIEW"}
          </span>
        </div>
        <div className="bg-[#0d1117] px-5 py-3 text-xs text-[#7d8590]">
          {form.company_display_name || "Your company name"} · {form.footer_text || "Footer text will appear here"}
        </div>
      </div>
    </div></DarkPage>
  );
}
